import { Route } from "react-router-dom";
import { ScreenProps } from "@components-common/types";
import { toDynamicRoute } from "../../components-common/_utils/helpers.screens";
import { layoutWorkspace } from "../../_website/_common/layout.workspace";
import { ErrorBoundary } from "taltech-styleguide";
import { protectedRoute } from "../../_runtime/routing/ProtectedRoute";
import { RoleConfig } from "../../components-fabric";
import { PublicLayout, WorkspaceLayout } from "../layouts";
import { RootieScreen } from "../layouts/screens/Screen";

export type DynamicRoute = {
  path: string;
  id: string;
  element?: any;
  loader?: () => Promise<Response>;
  roles?: RoleConfig;
  protected?: boolean;
  components: any;
};
const defaultWrapperComponents: ScreenProps<any>["components"] = {
  layout: [layoutWorkspace],
  errorBoundary: ErrorBoundary,
  protected: protectedRoute,
};

/**
 * Function: createRouteFromScreenProps
 *
 * This function creates a route configuration object based on the provided screen properties and
 * user authentication status.
 *
 * @param {ScreenProps} props - The properties of the screen.
 * @param {boolean} isAuthenticated - Indicates if the user is authenticated.
 * @param {string | undefined} role - The role of the authenticated user.
 *
 * Returns:
 * @returns {Object | undefined} The route configuration object or undefined.
 */
export const createRouteFromScreenProps = (
  props: ScreenProps,
  isAuthenticated: boolean,
  role: string | undefined
) => {
  let {
    path,
    id,
    key,
    element = RootieScreen,
    roles = [],
    protected: protectedValue = false,
    components = defaultWrapperComponents,
    ...rest
  } = toDynamicRoute(props);

  if (!isAuthenticated) {
    if (
      props.id.includes("public") ||
      props.id.includes("errors") ||
      props.id.includes("auth")
    ) {
      const {
        layout = layoutWorkspace,
        errorBoundary: ErrorComponent = ErrorBoundary,
        protected: protectedRouteComponent = protectedRoute,
      } = components!;
      const routeConfig = {
        element,
        roles,
        protected: protectedValue,
        components: {
          layout: [layout],
          errorBoundary: ErrorComponent,
          protected: protectedRouteComponent,
        },
        id,
        key,
        path,
      };

      return routeConfig;
    }
  } else {
    const {
      layout = layoutWorkspace,
      errorBoundary: ErrorComponent = ErrorBoundary,
      protected: protectedRouteComponent = protectedRoute,
    } = components!;
    const routeConfig = {
      element,
      roles,
      protected: protectedValue,
      components: {
        layout: [layout],
        errorBoundary: ErrorComponent,
        protected: protectedRouteComponent,
      },
      id,
      key,
      path,
    };

    return routeConfig;
  }
};

/**
 * Function: buildNestedRoutes
 *
 * This function builds nested routes from an array of dynamic route properties.
 *
 * @param {DynamicRoute[]} routeProps - The properties of the dynamic routes.
 *
 * Returns:
 * @returns {JSX.Element[]} An array of JSX elements representing the nested routes.
 */
export const buildNestedRoutes = (routeProps: DynamicRoute[]) => {
  const createRoutes = (routesParam: DynamicRoute[]): JSX.Element[] => {
    return routesParam.map((elem) => {
      const {
        id,
        element: Elem,
        path,
        components: { layout = [], ...rest } = {},
      } = elem;

      const route = (
        <Route
          key={id}
          element={<Elem id={id} />}
          path={path}
          handle={{
            id: (data: any) => console.log(data),
            screenId: id,
          }}
        />
      );
      if (layout.length > 0) {
        const singleLayout = layout[0];

        if (
          singleLayout.type === "workspace" ||
          singleLayout.type === "course"
        ) {
          return (
            <Route key={id} element={<WorkspaceLayout {...singleLayout} />}>
              {route}
            </Route>
          );
        } else {
          return (
            <Route key={id} element={<PublicLayout {...singleLayout} />}>
              {route}
            </Route>
          );
        }
      }
      return route;
    });
  };
  const routesResult = createRoutes(routeProps);
  return routesResult;
};
